import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";

import StoryEditorSidebar from "../StoryEditor/Sidebar";
import ContentContainer from "modules/ContentContainer";
import { installationManagerMapStateToProps } from "mapToProps/kioskManager";
import * as installationActions from "actions/kiosks";
import "./InstallationManager.scss";
import { PublishStatusDialog } from "./PublishStatusDialog";
import SystemReport from "./SystemReport";
import { PublishManager } from "./PublishManager";
import { PublishHistory2 } from "./PublishHistory2";

export class InstallationManager extends Component {
  constructor(props) {
    super(props);

      this.state = {
        isPublishStatusModalOpen: false,
        selectedPublishId: null,
        // When true, the dialog is open but waiting for an in-progress publish
        // entry to appear in the data so it can lock onto the correct publishId.
        awaitingPublishId: false
      };

    // Poll control for installation data refresh
    this._installationDataInterval = null;

    this.handlePublishCancelClick = this.handlePublishCancelClick.bind(this);
    this.handleViewDetailsClick = this.handleViewDetailsClick.bind(this);
    this.handleViewDetailsClickWithId = this.handleViewDetailsClickWithId.bind(this);
    this.startUptimePolling = this.startUptimePolling.bind(this);
    this.stopUptimePolling = this.stopUptimePolling.bind(this);
    this.handlePublishStatusDialogClose =
      this.handlePublishStatusDialogClose.bind(this);
  }

  componentDidMount() {
    this.props.requestInstallation();
    this.props.requestProjects();
    this.props.requestKioskAppVersions();
    this.startUptimePolling();
  }

  componentDidUpdate(prevProps, prevState) {
    /*
    If the installationRevertCopy prop is undefined, that means this component has never 
    been mounted before, and a revert copy hasn't been created before. As long as
    the installation data is defined, that means the store has been populated, and the 
    installation data is accessible to be copied.

    Otherwise, if the installationRevertCopy is defined and the ID of that installation doesn't
    match the installationID prop, that means a new installation is being edited, and a new 
    revert copy needs to be created.
    */
    if (typeof this.props.installationRevertCopy === "undefined") {
      if (typeof this.props.installationData !== "undefined") {
        this.props.createInstallationRevertCopy(this.props.installationID);
      }
    } else {
      if (this.props.installationID !== this.props.installationRevertCopy._id) {
        this.props.createInstallationRevertCopy(this.props.installationID);
      }
    }

    // When the dialog is open awaiting a publishId, scan from the end of
    // publishHistory for the most recent in-progress entry. This avoids a
    // race condition where the data may already contain the new publish by
    // the time the user clicks "View details."
    if (this.state.isPublishStatusModalOpen && !this.state.selectedPublishId && this.state.awaitingPublishId) {
      const { installationData } = this.props;
      if (installationData && installationData.publishHistory && installationData.publishHistory.length > 0) {
        const history = installationData.publishHistory;
        for (let i = history.length - 1; i >= 0; i--) {
          const entry = history[i];
          const isComplete = entry.status === "complete";
          const isError = entry.metadata && entry.metadata.error;
          if (entry.publishId && !isComplete && !isError) {
            this.setState({ selectedPublishId: entry.publishId, awaitingPublishId: false });
            break;
          }
        }
      }
    }
  }

  componentWillUnmount() {
    this.stopUptimePolling();
  }

  handlePublishCancelClick() {
    const { installationData, updatePublishHistoryEntry } = this.props;
    
    if (!installationData) {
      console.warn("Cannot cancel publish: installation data not available");
      return;
    }

    // Get the current publish status
    const currentStatus = this.getCurrentPublishStatus();
    
    // Find the most recent publishId from publishHistory that matches the current status
    const publishHistory = installationData.publishHistory || [];
    const currentEntry = publishHistory
      .filter(entry => entry.status === currentStatus)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];

    if (!currentEntry || !currentEntry.publishId) {
      console.warn("Cannot cancel publish: no matching publish history entry found");
      return;
    }

    const metadata = {
      cancelledAt: new Date()
    };

    // Update the publish history entry
    updatePublishHistoryEntry(
      installationData._id,
      currentEntry.publishId,
      currentEntry.stage,
      currentStatus,
      metadata
    );
  }

  startUptimePolling() {
    // Stop any existing polling first to avoid multiple intervals
    this.stopUptimePolling();

    // Set up polling to fetch installation data every 8 seconds
    this._installationDataInterval = setInterval(() => {
      this.props.requestInstallation({ lastSeenTS: true });
    }, 8000);
  }

  stopUptimePolling() {
    if (this._installationDataInterval) {
      clearInterval(this._installationDataInterval);
      this._installationDataInterval = null;
    }
  }

  handleViewDetailsClick = (isNewPublish) => {
    const { installationData } = this.props;
    const publishHistory = installationData && installationData.publishHistory;

    // When a publish was just initiated, publishHistory may still contain the
    // previous publish's data. Open the dialog with no publishId and let
    // componentDidUpdate detect the first in-progress entry.
    if (isNewPublish) {
      this.setState({
        isPublishStatusModalOpen: true,
        selectedPublishId: null,
        awaitingPublishId: true
      });
      return;
    }

    const mostRecentPublishId = publishHistory && publishHistory.length > 0
      ? publishHistory[publishHistory.length - 1].publishId
      : null;
    if (!mostRecentPublishId) {
      console.warn("Cannot view details: no publish history entry found");
      return;
    }
    this.setState({
      isPublishStatusModalOpen: true,
      selectedPublishId: mostRecentPublishId,
      awaitingPublishId: false
    });
  };

  handleViewDetailsClickWithId = (publishId) => {
    this.setState({
      isPublishStatusModalOpen: true,
      selectedPublishId: publishId
    });
  };

  handlePublishStatusDialogClose = () => {
    this.setState({ 
      isPublishStatusModalOpen: false,
      selectedPublishId: null,
      awaitingPublishId: false
    });
  };

  /**
   * Gets the current publish status to display, resetting to "preparing" if a new publish was just initiated
   * @returns {String} The publish status to display
   */
  getCurrentPublishStatus() {
    const { installationData } = this.props;
    const { selectedPublishId, awaitingPublishId } = this.state;

    // Dialog is open but waiting for an in-progress entry to appear;
    // show "preparing" rather than the stale status from the previous publish.
    if (awaitingPublishId) {
      return "preparing";
    }

    if (selectedPublishId && installationData && installationData.publishHistory) {
      const publishHistory = installationData.publishHistory;
      const entriesForPublishId = publishHistory.filter(entry => entry.publishId === selectedPublishId);
      if (entriesForPublishId.length > 0) {
        const sortedEntries = entriesForPublishId.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        return sortedEntries[sortedEntries.length - 1].status;
      }
    }

    return installationData ? installationData.publishStatus : "preparing";
  }

  /**
   * Gets the publish history entries for a specific publishId
   * @param {String} publishId - The publishId to get history for
   * @returns {Array} Array of history entries for the publishId
   */
  getPublishHistoryForId(publishId) {
    const { installationData } = this.props;
    if (!publishId || !installationData || !installationData.publishHistory) {
      return [];
    }
    return installationData.publishHistory
      .filter(entry => entry.publishId === publishId)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  }

  render() {
    const {
      installationData,
      section,
      userPreferences,
      userPreferencesSelector,
      sidebarProps,
      sidebarMode,
      installationType,
      settings,
      allProjects
    } = this.props;

    const SidebarActionBar = (
      <div>
        <div className="story-type-container">
          {installationData ? installationData.title : ""}
        </div>
      </div>
    );

    let sidebarActions = null;
    if (section === "settings") {
      sidebarActions = {
        handleInputChange: (prop, value, isLocalized) =>
          this.props.handleSettingsInputChange(prop, value, isLocalized),
        handleSave: () => {
          this.props.createInstallationRevertCopy();
          this.props.saveInstallation("settings");
        },
        handleRevert: () => this.props.revertInstallationChanges(),
        changeLanguage: value => this.props.changeLanguage(value),
        languages: this.props.languages,
        langID: this.props.langID,
        saveLabel: "Save",
        showButtons: true
      };
    }

    const topBar = (
      <div
        className={`status-indicator ${
          installationData && installationData.isOnline ? "online" : "offline"
        }`}
      >
        <div className="icon" />
        <div className="label">
          {installationData && installationData.isOnline ? "Online" : "Offline"}
        </div>
      </div>
    );

    let sidebarChildren;
    if (section === "systemReport") {
      sidebarChildren = null;
    } else if (section === "settings") {
      sidebarChildren = null;
    }

    const className =
      section === "systemReport"
        ? "kiosk-system-report"
        : section === "settings"
        ? "kiosk-settings"
        : "kiosk-manager";

    return (
      <div className={`cms-screen ${className}`}>
        <Helmet>
          <title>
            {section === undefined
              ? "Manage Kiosk"
              : section === "systemReport"
              ? "Kiosk: System Report"
              : section === "settings"
              ? "Kiosk: Settings"
              : "Kiosk: TBD"}
          </title>
        </Helmet>

        <StoryEditorSidebar
          {...sidebarProps}
          actionBar={SidebarActionBar}
          {...sidebarActions}
        >
          {sidebarChildren}
        </StoryEditorSidebar>

        <ContentContainer
          className="content-container"
          sidebarMode={sidebarMode}
          topBarAlignContentRight={false}
          topBar={topBar}
        >
          {section === "systemReport" ? (
            <SystemReport
              installationData={installationData}
              installationType={installationType}
              settings={settings}
            />
          ) :
          section === "settings" ? (
            null
          ) : (
            <div>
              <PublishManager
                projects={allProjects}
                installationData={installationData}
                handleSettingsInputChange={this.props.handleSettingsInputChange}
                saveInstallation={this.props.saveInstallation}
                publishInstallationProject={this.props.publishInstallationProject}
                requestInstallation={this.props.requestInstallation}
                handlePublishCancelClick={this.handlePublishCancelClick}
                handleViewDetailsClick={this.handleViewDetailsClick}
              />
              <PublishHistory2
                installationData={installationData}
                projects={allProjects}
                handleViewDetailsClick={this.handleViewDetailsClickWithId}
                onRevertClick={this.props.revertInstallationBundle}
              />
            </div>
          )}
        </ContentContainer>

        <PublishStatusDialog
          isOpen={this.state.isPublishStatusModalOpen}
          publishStatus={this.getCurrentPublishStatus()}
          publishId={this.state.selectedPublishId}
          publishHistory={this.state.selectedPublishId ? this.getPublishHistoryForId(this.state.selectedPublishId) : null}
          onClose={this.handlePublishStatusDialogClose}
        />
      </div>
    );
  }
}

InstallationManager.defaultProps = {
  isLoading: false
};

InstallationManager.propTypes = {
  isLoading: PropTypes.bool
};

function mapStateToProps(state, ownProps) {
  return installationManagerMapStateToProps(state, ownProps);
}

function mergeProps(stateProps, dispatchProps, ownProps) {
  const { installationID, installationType, username } = stateProps;

  return Object.assign({}, stateProps, {
    // Replace with logic for loading installation data.
    requestProjects: () => dispatchProps.requestProjects(username),
    requestInstallation: (filters) => {
      return dispatchProps.installationFetchRequest(installationID, filters || null);
    },
    publishInstallationProject: () => {
      dispatchProps.installationPublishRequest(installationID);
    },
    handleSettingsInputChange: (prop, value) => {
      dispatchProps.setInstallationProp(installationID, prop, value);
    },
    saveInstallation: saveType => {
      dispatchProps.installationUpdateRequest(installationID, saveType);
    },
    createInstallationRevertCopy: () =>
      dispatchProps.createInstallationRevertCopy(installationID),
    revertInstallationChanges: () => dispatchProps.revertInstallationChanges(),
    updatePublishHistoryEntry: (installationID, publishID, stage, status, metadata) => {
      dispatchProps.installationUpdateHistoryEntry(installationID, publishID, stage, status, metadata);
    },
    revertInstallationBundle: (bundleID) =>
      dispatchProps.installationRevertRequest(installationID, bundleID),
    requestKioskAppVersions: () => dispatchProps.kioskAppVersionsFetchRequest()
  });
}

export default connect(
  mapStateToProps,
  installationActions,
  mergeProps
)(InstallationManager);
